import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Edit3,
  Loader2,
  LockKeyhole,
  MailPlus,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  UsersRound,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { adminAPI } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { getPermissionLabel } from '../../utils/rbac';

const tabs = [
  { id: 'users', label: 'Admin Users', icon: UsersRound },
  { id: 'roles', label: 'Roles', icon: ShieldCheck },
  { id: 'permissions', label: 'Permission Catalog', icon: LockKeyhole }
];

const emptyInviteForm = {
  firstName: '',
  lastName: '',
  email: '',
  roleId: ''
};

const emptyRoleForm = {
  id: null,
  name: '',
  description: '',
  permissions: [],
  status: 'active'
};

const emptyAdminForm = {
  firstName: '',
  lastName: '',
  roleId: '',
  status: 'active',
  statusReason: ''
};

const extractError = (error, fallback) => error?.message || error?.response?.error || fallback;

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const labelize = (value = '') => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bRbac\b/g, 'RBAC')
  .replace(/\bKyc\b/g, 'KYC');

const statusClasses = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  disabled: 'border-slate-200 bg-white text-slate-600',
  suspended: 'border-rose-200 bg-rose-50 text-rose-700',
  invited: 'border-slate-200 bg-white text-slate-600'
};

const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 ${className}`}>
    {children}
  </span>
);

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${statusClasses[status] || statusClasses.disabled}`}>
    {labelize(status)}
  </span>
);

const PermissionPill = ({ permission }) => (
  <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
    {permission === '*' ? 'All permissions' : getPermissionLabel(permission)}
  </span>
);

const EmptyState = ({ children }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm leading-6 text-slate-500">
    {children}
  </div>
);

const StatTile = ({ label, value, detail }) => (
  <article className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-6">
      <span className="text-sm font-semibold text-slate-600">{label}</span>
    </div>
    <div>
      <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      {detail && <p className="mt-2 text-xs font-medium text-slate-400">{detail}</p>}
    </div>
  </article>
);

const PageSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8" aria-label="Loading admin RBAC">
    <div className="mb-6 space-y-3">
      <SkeletonBlock className="h-4 w-48 rounded-md" />
      <SkeletonBlock className="h-10 w-80 rounded-lg" />
      <SkeletonBlock className="h-4 w-[44rem] max-w-full rounded-md" />
    </div>
    <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => <SkeletonBlock key={index} className="h-24 rounded-lg" />)}
    </div>
    <div className="mb-5 flex gap-2">
      {tabs.map((tab) => <SkeletonBlock key={tab.id} className="h-10 w-40 rounded-lg" />)}
    </div>
    <section className="rounded-[24px] border border-slate-200 bg-white shadow-sm">
      <div className="space-y-2 p-4">
        {Array.from({ length: 8 }).map((_, index) => <SkeletonBlock key={index} className="h-14 rounded-md" />)}
      </div>
    </section>
  </div>
);

const Modal = ({ open, title, subtitle, children, footer, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section role="dialog" aria-modal="true" aria-label={title} className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h2>
            {subtitle && <p className="mt-1 text-sm leading-6 text-slate-500">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-900 hover:bg-slate-50" aria-label="Close modal">
            <X size={17} />
          </button>
        </header>
        <div className="max-h-[calc(92vh-152px)] overflow-y-auto p-5">{children}</div>
        {footer && <footer className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 p-4 sm:flex-row sm:justify-end">{footer}</footer>}
      </section>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-medium text-slate-700">{label}</span>
    {children}
  </label>
);

const inputClass = 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50 disabled:text-slate-500';
const textareaClass = 'min-h-[96px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100';

function AdminsRbacPage() {
  const currentUser = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState('users');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(emptyInviteForm);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [adminModal, setAdminModal] = useState(null);
  const [adminForm, setAdminForm] = useState(emptyAdminForm);
  const [savingInvite, setSavingInvite] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [deletingRoleId, setDeletingRoleId] = useState(null);

  const rbacQuery = useQuery(
    ['admin-rbac'],
    async () => {
      const [permissions, roles, admins] = await Promise.all([
        adminAPI.getAdminPermissions(),
        adminAPI.getAdminRoles(),
        adminAPI.getAdminUsers()
      ]);
      return {
        permissions: permissions.permissions || [],
        roles: roles.roles || [],
        admins: admins.admins || []
      };
    },
    {
      staleTime: 30000,
      refetchOnWindowFocus: false
    }
  );

  const permissions = useMemo(() => rbacQuery.data?.permissions || [], [rbacQuery.data?.permissions]);
  const roles = useMemo(() => rbacQuery.data?.roles || [], [rbacQuery.data?.roles]);
  const admins = useMemo(() => rbacQuery.data?.admins || [], [rbacQuery.data?.admins]);
  const activeRoles = useMemo(() => roles.filter((role) => role.status === 'active'), [roles]);
  const permissionGroups = useMemo(() => permissions.reduce((groups, permission) => {
    const key = permission.category || 'Other';
    groups[key] = [...(groups[key] || []), permission];
    return groups;
  }, {}), [permissions]);

  const activeAdminCount = admins.filter((admin) => admin.status === 'active').length;
  const disabledAdminCount = admins.filter((admin) => admin.status !== 'active').length;
  const customRoleCount = roles.filter((role) => !role.isSystem && !role.isSuperAdmin).length;

  useEffect(() => {
    if (!inviteForm.roleId && activeRoles.length) {
      setInviteForm((current) => ({ ...current, roleId: activeRoles.find((role) => !role.isSuperAdmin)?.id || activeRoles[0]?.id || '' }));
    }
  }, [activeRoles, inviteForm.roleId]);

  const refresh = () => rbacQuery.refetch();

  const togglePermission = (key) => {
    setRoleForm((current) => {
      const exists = current.permissions.includes(key);
      return {
        ...current,
        permissions: exists
          ? current.permissions.filter((permission) => permission !== key)
          : [...current.permissions, key]
      };
    });
  };

  const openNewRole = () => {
    setRoleForm(emptyRoleForm);
    setRoleModalOpen(true);
    setActiveTab('roles');
  };

  const editRole = (role) => {
    setActiveTab('roles');
    setRoleForm({
      id: role.id,
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions?.includes('*') ? permissions.map((permission) => permission.key) : (role.permissions || []),
      status: role.status || 'active'
    });
    setRoleModalOpen(true);
  };

  const openAdminEditor = (admin) => {
    setAdminModal(admin);
    setAdminForm({
      firstName: admin.firstName || '',
      lastName: admin.lastName || '',
      roleId: admin.role?.id || '',
      status: admin.status || 'active',
      statusReason: admin.statusReason || ''
    });
  };

  const saveRole = async (event) => {
    event.preventDefault();
    if (savingRole) return;
    setSavingRole(true);
    try {
      const payload = {
        name: roleForm.name.trim(),
        description: roleForm.description.trim(),
        permissions: roleForm.permissions,
        status: roleForm.status
      };

      if (roleForm.id) {
        await adminAPI.updateAdminRole(roleForm.id, payload);
        toast.success('Admin role updated.');
      } else {
        await adminAPI.createAdminRole(payload);
        toast.success('Admin role created.');
      }
      setRoleForm(emptyRoleForm);
      setRoleModalOpen(false);
      await refresh();
    } catch (error) {
      toast.error(extractError(error, 'Role save failed.'));
    } finally {
      setSavingRole(false);
    }
  };

  const deleteRole = async (role) => {
    if (role.isSystem || role.isSuperAdmin || Number(role.userCount || 0) > 0) return;
    if (!window.confirm(`Delete admin role "${role.name}"?`)) return;
    setDeletingRoleId(role.id);
    try {
      await adminAPI.deleteAdminRole(role.id);
      toast.success('Admin role deleted.');
      if (Number(roleForm.id) === Number(role.id)) setRoleForm(emptyRoleForm);
      await refresh();
    } catch (error) {
      toast.error(extractError(error, 'Role delete failed.'));
    } finally {
      setDeletingRoleId(null);
    }
  };

  const inviteAdmin = async (event) => {
    event.preventDefault();
    if (savingInvite) return;
    setSavingInvite(true);
    try {
      await adminAPI.inviteAdminUser({
        ...inviteForm,
        roleId: Number(inviteForm.roleId)
      });
      toast.success('Admin invited. Temporary password email queued.');
      setInviteForm({
        ...emptyInviteForm,
        roleId: activeRoles.find((role) => !role.isSuperAdmin)?.id || activeRoles[0]?.id || ''
      });
      setInviteOpen(false);
      await refresh();
    } catch (error) {
      toast.error(extractError(error, 'Admin invitation failed.'));
    } finally {
      setSavingInvite(false);
    }
  };

  const saveAdmin = async (event) => {
    event.preventDefault();
    if (!adminModal || updatingUserId) return;
    setUpdatingUserId(adminModal.id);
    try {
      await adminAPI.updateAdminUser(adminModal.id, {
        firstName: adminForm.firstName,
        lastName: adminForm.lastName,
        roleId: Number(adminForm.roleId),
        status: adminForm.status,
        statusReason: adminForm.statusReason
      });
      toast.success('Admin user updated.');
      setAdminModal(null);
      setAdminForm(emptyAdminForm);
      await refresh();
    } catch (error) {
      toast.error(extractError(error, 'Admin user update failed.'));
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (rbacQuery.isLoading) return <PageSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="Admins & RBAC"
        description="Manage platform administrators, access roles, and permission gates with clear review surfaces instead of inline table sprawl."
      />

      {rbacQuery.isError && (
        <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-900" />
            <p>{extractError(rbacQuery.error, 'Unable to load admin RBAC data.')}</p>
          </div>
        </div>
      )}

      <section className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Admin accounts" value={formatNumber(admins.length)} detail={`${formatNumber(activeAdminCount)} active, ${formatNumber(disabledAdminCount)} restricted`} />
        <StatTile label="Roles" value={formatNumber(roles.length)} detail={`${formatNumber(customRoleCount)} custom roles`} />
        <StatTile label="Permissions" value={formatNumber(permissions.length)} detail={`${formatNumber(Object.keys(permissionGroups).length)} categories`} />
        <StatTile label="Active role pool" value={formatNumber(activeRoles.length)} detail="Assignable roles for invitations" />
      </section>

      <nav className="mb-5 overflow-x-auto rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
        <div className="flex min-w-max gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-medium transition ${
                  active ? 'bg-slate-100 text-slate-900 shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {activeTab === 'users' && (
        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Admin users</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">Open a row to edit identity, role assignment, and account status.</p>
            </div>
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              disabled={!activeRoles.length}
            >
              <MailPlus size={16} /> Invite admin
            </button>
          </div>
          {admins.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-[1080px] divide-y divide-slate-100 text-left">
                <thead className="bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Admin</th>
                    <th className="px-6 py-4">Role</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Security</th>
                    <th className="px-6 py-4">Last login</th>
                    <th className="px-6 py-4 text-right">Open</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {admins.map((admin) => {
                    const isSelf = Number(currentUser?.id) === Number(admin.id);
                    const displayName = [admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.email;
                    return (
                      <tr key={admin.id} className="transition hover:bg-slate-50">
                        <td className="px-6 py-4 align-top">
                          <p className="text-sm font-medium text-slate-900">{displayName}</p>
                          <p className="mt-1 text-xs text-slate-500">{admin.email}</p>
                          {isSelf && <div className="mt-2"><Badge>Current session</Badge></div>}
                        </td>
                        <td className="px-6 py-4 align-top">
                          <p className="text-sm font-medium text-slate-900">{admin.role?.name || 'No role'}</p>
                          <div className="mt-2 flex max-w-[320px] flex-wrap gap-1.5">
                            {(admin.permissions || []).slice(0, 3).map((permission) => <PermissionPill key={permission} permission={permission} />)}
                            {(admin.permissions || []).length > 3 && <PermissionPill permission={`+${admin.permissions.length - 3} more`} />}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <StatusBadge status={admin.status} />
                          {admin.statusReason && <p className="mt-2 max-w-[220px] truncate text-xs text-slate-500">{admin.statusReason}</p>}
                        </td>
                        <td className="px-6 py-4 align-top">
                          {admin.email2faEnabled ? <Badge>Email 2FA</Badge> : <Badge>2FA not enabled</Badge>}
                          <p className="mt-2 text-xs text-slate-500">Invited {formatDateTime(admin.invitedAt)}</p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 align-top text-sm text-slate-600">{formatDateTime(admin.lastLoginAt)}</td>
                        <td className="whitespace-nowrap px-4 py-3 align-top text-right">
                          <button
                            type="button"
                            onClick={() => openAdminEditor(admin)}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 hover:bg-slate-50"
                          >
                            <Edit3 size={14} /> Manage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4"><EmptyState>No admin users are configured.</EmptyState></div>
          )}
        </section>
      )}

      {activeTab === 'roles' && (
        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Roles</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">System roles are protected. Custom roles can be edited or deleted when unused.</p>
            </div>
            <button type="button" onClick={openNewRole} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-medium text-white hover:bg-slate-800">
              <Plus size={16} /> New role
            </button>
          </div>
          {roles.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-[1080px] divide-y divide-slate-100 text-left">
                <thead className="bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Role</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Users</th>
                    <th className="px-6 py-4">Permissions</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {roles.map((role) => (
                    <tr key={role.id} className="transition hover:bg-slate-50">
                      <td className="px-6 py-4 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-slate-900">{role.name}</p>
                          {role.isSuperAdmin && <Badge>Super Admin</Badge>}
                          {role.isSystem && <Badge>System</Badge>}
                        </div>
                        {role.description && <p className="mt-1 max-w-xl text-xs leading-5 text-slate-500">{role.description}</p>}
                      </td>
                      <td className="px-6 py-4 align-top"><StatusBadge status={role.status} /></td>
                      <td className="px-6 py-4 align-top text-sm text-slate-600">{formatNumber(role.userCount || 0)}</td>
                      <td className="px-6 py-4 align-top">
                        <div className="flex max-w-[520px] flex-wrap gap-1.5">
                          {(role.permissions || []).slice(0, 6).map((permission) => <PermissionPill key={permission} permission={permission} />)}
                          {(role.permissions || []).length > 6 && <PermissionPill permission={`+${role.permissions.length - 6} more`} />}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-right">
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            onClick={() => editRole(role)}
                            disabled={role.isSuperAdmin}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Edit3 size={14} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteRole(role)}
                            disabled={role.isSystem || role.isSuperAdmin || Number(role.userCount || 0) > 0 || deletingRoleId === role.id}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Delete role"
                          >
                            {deletingRoleId === role.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4"><EmptyState>No roles are configured.</EmptyState></div>
          )}
        </section>
      )}

      {activeTab === 'permissions' && (
        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-4">
            <h2 className="text-sm font-semibold text-slate-900">Permission catalog</h2>
            <p className="mt-1 text-xs leading-5 text-slate-500">Canonical permission keys used by Admin route gates and role assignment.</p>
          </div>
          {permissions.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-[920px] divide-y divide-slate-100 text-left">
                <thead className="bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Permission</th>
                    <th className="px-6 py-4">Key</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {permissions.map((permission) => (
                    <tr key={permission.key} className="transition hover:bg-slate-50">
                      <td className="px-6 py-4 align-top text-sm font-medium text-slate-900">{permission.label}</td>
                      <td className="px-6 py-4 align-top"><code className="rounded-md bg-slate-950 px-2 py-1 text-xs font-medium text-white">{permission.key}</code></td>
                      <td className="px-6 py-4 align-top"><Badge>{permission.category}</Badge></td>
                      <td className="px-6 py-4 align-top text-sm leading-6 text-slate-600">{permission.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4"><EmptyState>No permission definitions are available.</EmptyState></div>
          )}
        </section>
      )}

      <Modal
        open={inviteOpen}
        title="Invite admin"
        subtitle="A temporary password is emailed to the new administrator after the invitation is created."
        onClose={() => {
          if (!savingInvite) setInviteOpen(false);
        }}
        footer={(
          <>
            <button type="button" onClick={() => setInviteOpen(false)} disabled={savingInvite} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-50 disabled:opacity-60">Cancel</button>
            <button type="submit" form="invite-admin-form" disabled={savingInvite || !activeRoles.length} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
              {savingInvite ? <Loader2 size={16} className="animate-spin" /> : <MailPlus size={16} />}
              Send invitation
            </button>
          </>
        )}
      >
        <form id="invite-admin-form" onSubmit={inviteAdmin} className="grid gap-4 md:grid-cols-2">
          <Field label="First name">
            <input type="text" value={inviteForm.firstName} onChange={(event) => setInviteForm((current) => ({ ...current, firstName: event.target.value }))} className={inputClass} required />
          </Field>
          <Field label="Last name">
            <input type="text" value={inviteForm.lastName} onChange={(event) => setInviteForm((current) => ({ ...current, lastName: event.target.value }))} className={inputClass} required />
          </Field>
          <Field label="Email">
            <input type="email" value={inviteForm.email} onChange={(event) => setInviteForm((current) => ({ ...current, email: event.target.value }))} className={inputClass} required />
          </Field>
          <Field label="Role">
            <select value={inviteForm.roleId} onChange={(event) => setInviteForm((current) => ({ ...current, roleId: event.target.value }))} className={inputClass} required>
              {activeRoles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
            </select>
          </Field>
        </form>
      </Modal>

      <Modal
        open={Boolean(adminModal)}
        title="Manage admin"
        subtitle={adminModal ? `${adminModal.email} / ${Number(currentUser?.id) === Number(adminModal.id) ? 'Current session' : 'Admin account'}` : ''}
        onClose={() => {
          if (!updatingUserId) setAdminModal(null);
        }}
        footer={(
          <>
            <button type="button" onClick={() => setAdminModal(null)} disabled={Boolean(updatingUserId)} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-50 disabled:opacity-60">Cancel</button>
            <button type="submit" form="admin-user-form" disabled={Boolean(updatingUserId)} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60">
              {updatingUserId ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save admin
            </button>
          </>
        )}
      >
        {adminModal && (
          <form id="admin-user-form" onSubmit={saveAdmin} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="First name">
                <input value={adminForm.firstName} onChange={(event) => setAdminForm((current) => ({ ...current, firstName: event.target.value }))} className={inputClass} required />
              </Field>
              <Field label="Last name">
                <input value={adminForm.lastName} onChange={(event) => setAdminForm((current) => ({ ...current, lastName: event.target.value }))} className={inputClass} required />
              </Field>
              <Field label="Role">
                <select value={adminForm.roleId} onChange={(event) => setAdminForm((current) => ({ ...current, roleId: event.target.value }))} disabled={Number(currentUser?.id) === Number(adminModal.id)} className={inputClass} required>
                  {activeRoles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={adminForm.status} onChange={(event) => setAdminForm((current) => ({ ...current, status: event.target.value }))} disabled={Number(currentUser?.id) === Number(adminModal.id)} className={inputClass}>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                  <option value="suspended">Suspended</option>
                </select>
              </Field>
            </div>
            <Field label="Status reason or internal note">
              <textarea value={adminForm.statusReason} onChange={(event) => setAdminForm((current) => ({ ...current, statusReason: event.target.value }))} className={textareaClass} placeholder="Document why this account status or role changed." />
            </Field>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-500">Effective permissions</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(adminModal.permissions || []).length
                  ? (adminModal.permissions || []).map((permission) => <PermissionPill key={permission} permission={permission} />)
                  : <span className="text-sm text-slate-500">No permissions reported for this admin.</span>}
              </div>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={roleModalOpen}
        title={roleForm.id ? 'Edit role' : 'Create role'}
        subtitle="Permissions map directly to Admin route gates. Keep roles narrow enough to audit."
        onClose={() => {
          if (!savingRole) setRoleModalOpen(false);
        }}
        footer={(
          <>
            <button type="button" onClick={() => setRoleModalOpen(false)} disabled={savingRole} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-50 disabled:opacity-60">Cancel</button>
            <button type="submit" form="admin-role-form" disabled={savingRole || !roleForm.permissions.length} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
              {savingRole ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save role
            </button>
          </>
        )}
      >
        <form id="admin-role-form" onSubmit={saveRole} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Role name">
              <input value={roleForm.name} onChange={(event) => setRoleForm((current) => ({ ...current, name: event.target.value }))} className={inputClass} required />
            </Field>
            {roleForm.id && (
              <Field label="Status">
                <select value={roleForm.status} onChange={(event) => setRoleForm((current) => ({ ...current, status: event.target.value }))} className={inputClass}>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </Field>
            )}
          </div>
          <Field label="Description">
            <textarea value={roleForm.description} onChange={(event) => setRoleForm((current) => ({ ...current, description: event.target.value }))} className={textareaClass} />
          </Field>
          <div className="space-y-4">
            {Object.entries(permissionGroups).map(([group, groupPermissions]) => (
              <section key={group} className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-3 text-xs font-medium uppercase tracking-[0.14em] text-slate-500">{group}</h3>
                <div className="grid gap-2 md:grid-cols-2">
                  {groupPermissions.map((permission) => (
                    <label key={permission.key} className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 transition hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={roleForm.permissions.includes(permission.key)}
                        onChange={() => togglePermission(permission.key)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-slate-900">{permission.label}</span>
                        <span className="mt-1 block text-xs leading-5 text-slate-500">{permission.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default AdminsRbacPage;
